import { createMultipartParser } from "./response-stream-multipart.js";
import { parseMimeType } from "@scalar/helpers/http/mime-type";
import { isStreamingContentType } from "@scalar/helpers/http/is-streaming-content-type";
import { getUtf8ByteLength } from "@scalar/helpers/string/get-utf8-byte-length";
//#region src/v2/blocks/response-block/helpers/response-stream.ts
/** Only opt recognized streaming media types into the reader path. */
var getResponseStreamFormat = (contentType) => {
	if (!isStreamingContentType(contentType)) return;
	const { essence, type, subtype } = parseMimeType(contentType);
	if (essence === "text/event-stream") return "text";
	if ([
		"application/jsonl",
		"application/x-ndjson",
		"application/json-lines"
	].includes(essence)) return "json-lines";
	if (subtype === "json-seq" || subtype.endsWith("+json-seq")) return "json-seq";
	return type === "multipart" ? "multipart" : void 0;
};
/** Bound unfinished records, including servers that never send a delimiter. */
var MAX_STREAM_RECORD_SIZE = 8388608;
/** Decode complete records while retaining partial UTF-8 characters across network chunks. */
var createResponseStreamParser = (contentType, emit) => {
	const format = getResponseStreamFormat(contentType);
	if (format === "multipart") return createMultipartParser(contentType, emit);
	const decoder = new TextDecoder("utf-8", { fatal: format !== "text" && format !== void 0 });
	let buffer = "";
	let recordBytes = 0;
	let started = false;
	let depth = 0;
	let quoted = false;
	let escaped = false;
	const flush = () => {
		const record = buffer;
		buffer = "";
		recordBytes = 0;
		depth = 0;
		quoted = false;
		escaped = false;
		if (!record.trim()) return;
		let formatted;
		try {
			const value = JSON.parse(record);
			if (format === "json-seq" && typeof value !== "object" && typeof value !== "string" && !/\s$/.test(record)) throw new Error("Truncated primitive");
			if (format === "json-seq" && value === null && !/\s$/.test(record)) throw new Error("Truncated null");
			formatted = `${JSON.stringify(value, null, 2)}\n`;
		} catch {
			formatted = `[Invalid JSON record]\n${record.trim()}\n`;
		}
		emit(formatted);
	};
	const consume = (text) => {
		if (format !== "json-lines" && format !== "json-seq") {
			emit(text);
			return;
		}
		for (const char of text) {
			if (format === "json-seq" && char === "") {
				flush();
				started = true;
				continue;
			}
			if (format === "json-seq" && !started) {
				if (char.trim()) throw new Error("JSON sequence record is missing its record separator.");
				continue;
			}
			buffer += char;
			recordBytes += getUtf8ByteLength(char);
			if (recordBytes > MAX_STREAM_RECORD_SIZE) throw new Error("Stream record exceeds the 8 MiB display limit.");
			if (format === "json-lines") {
				if (char === "\n") flush();
				continue;
			}
			if (quoted) {
				if (escaped) escaped = false;
				else if (char === "\\") escaped = true;
				else if (char === "\"") quoted = false;
			} else if (char === "\"") quoted = true;
			else if (char === "{" || char === "[") depth++;
			else if (char === "}" || char === "]") depth--;
			if (char === "\n" && !quoted && depth === 0 && buffer.trim()) {
				flush();
				started = false;
			}
		}
	};
	return {
		push: (chunk) => consume(decoder.decode(chunk, { stream: true })),
		finish: () => {
			consume(decoder.decode());
			flush();
		}
	};
};
//#endregion
export { createResponseStreamParser, getResponseStreamFormat };

//# sourceMappingURL=response-stream.js.map